import type { QueryClient } from '@tanstack/react-query';
import {
  getGetTasksQueryKey,
  getGetExpoQueryKey,
  type KdsTaskDetail,
  type KdsTaskDetailStatus,
} from '@ordr-as/ordr-service-types/epos';

const INVALIDATE_DEBOUNCE_MS = 300;
// Even under a continuous burst, never let the board go stale longer than this.
const INVALIDATE_MAX_WAIT_MS = 1_000;

// `kds.tasks.added` and `kds.task.updated` share this shape.
type TaskEventPayload = {
  stationId: string;
  tasks: {
    taskId: string;
    stationId: string;
    eventType: string;
    payload: {
      bookingOrderId: number;
      groupId: string;
      status: string;
    };
  }[];
};

/**
 * Interprets KDS websocket messages and syncs them into the react-query caches
 * (both the flat tasks list and the expo read model). This is the only place
 * that knows both the wire payloads and the cache layout; the websocket client
 * stays a plain transport.
 */
export function createKdsTasksCacheSync(queryClient: QueryClient, locationId: number) {
  const tasksQueryKey = getGetTasksQueryKey(locationId);
  const expoQueryKey = getGetExpoQueryKey(locationId);
  let debounceTimer: number | undefined;
  let maxWaitTimer: number | undefined;

  const flush = () => {
    clearTimeout(debounceTimer);
    clearTimeout(maxWaitTimer);
    debounceTimer = undefined;
    maxWaitTimer = undefined;
    queryClient.invalidateQueries({ queryKey: tasksQueryKey });
    queryClient.invalidateQueries({ queryKey: expoQueryKey });
  };

  const scheduleInvalidate = () => {
    // Debounced so a burst collapses to one request, but maxWait guarantees a
    // flush during a sustained stream that would otherwise keep resetting the timer.
    clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(flush, INVALIDATE_DEBOUNCE_MS);
    if (maxWaitTimer === undefined) {
      maxWaitTimer = window.setTimeout(flush, INVALIDATE_MAX_WAIT_MS);
    }
  };

  const onSubscribed = () => {
    // GET is the source of truth for current state; refetch at the (re)subscribe
    // sync point and let the socket only drive updates after.
    scheduleInvalidate();
  };

  const onTaskEvent = (payload: TaskEventPayload) => {
    // The push payload has no items, so only patch statuses of tasks we already
    // know in the flat cache (never insert skeletons), and refetch full truth.
    const statusByTaskId = new Map(payload.tasks.map((t) => [Number(t.taskId), t.payload.status]));
    queryClient.setQueryData<KdsTaskDetail[]>(tasksQueryKey, (current) =>
      current?.map((task) => {
        const status = task.id === undefined ? undefined : statusByTaskId.get(task.id);
        return status ? { ...task, status: status as KdsTaskDetailStatus } : task;
      })
    );

    scheduleInvalidate();
  };

  return {
    onMessage: (type: string, data: unknown) => {
      if (type === 'subscribe.v1.kds.ok') onSubscribed();
      if (type === 'kds.tasks.added') onTaskEvent(data as TaskEventPayload);
      if (type === 'kds.task.updated') onTaskEvent(data as TaskEventPayload);
    },
    dispose: () => {
      clearTimeout(debounceTimer);
      clearTimeout(maxWaitTimer);
    },
  };
}
